/*
 * Copyright (c) 2017, Indigen Solutions. All rights reserved.
 *
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 *
 * This software is the confidential and proprietary information.
 *
 * You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement
 * you entered into.
 */

import { forEach, isArray } from "lodash";

import { Resource } from "./Resource";
import { Model } from "./Model";
import { RawResource } from "./RawResource";
import { Document, SingleDocument, ArrayDocument } from "./Document";
import { JsonApiService } from "./JsonApiService";
import { RawDocument } from "./RawDocument";
import { RawResourceIdentifier } from "./RawResourceIdentifier";
import { ResourceIdentifier } from "./ResourceIdentifier";

/**
 *
 */
export class DocumentBuilder<T extends Model> {

    private document: Document<T>;

    /**
     *
     */
    constructor(rawDocument: RawDocument, private jsonApiService: JsonApiService) {

        // Create document.
        if ( rawDocument.data ) {
            if (isArray(rawDocument.data)) {
                this.document = new ArrayDocument<T>();
                const data: Resource<T>[] = (rawDocument.data as RawResource[]).map(rawResource => this._createResource(rawResource));
                data.forEach((r) => (this.document as ArrayDocument<T>).addResource(r));
            } else {
                this.document = new SingleDocument<T>();
                const data: Resource<T> = this._createResource(rawDocument.data as RawResource);
                (this.document as SingleDocument<T>).setResource(data);
            }
        } else {
            this.document = new SingleDocument<T>();
        }

        // Complete document.
        if ( rawDocument.errors ) {
            this.document.setErrors(rawDocument.errors);
        }
        if ( rawDocument.meta ) {
            this.document.setMeta(rawDocument.meta);
        }

        // Add map for included.
        if (rawDocument.included) {
            rawDocument.included.forEach(rawResource => {
                this.document.include(this._createResource(rawResource));
            });
        }
    }

    /**
     *
     */
    getDocument(): Document<T> {
        return this.document;
    }

    /* Implementation. */

    private _createResource(rawResource: RawResource): Resource<T> {

        // Create model.
        const constructor = this.jsonApiService.getModelConstructor(rawResource.type);
        const model: T = new constructor() as T;
        model.id = rawResource.id;
        if (model.setAttributes) {
            model.setAttributes(rawResource.attributes);
        } else {
            Object.assign(model, rawResource.attributes);
        }

        // Handle relationships.
        const document = this.document;

        forEach(rawResource.relationships, (rawRelationship, name) => {
            if (isArray(rawRelationship.data)) {
                Object.defineProperty(model, name, {
                    get(): Model[] {
                        return (rawRelationship.data as RawResourceIdentifier[])
                            .map(identifier => new ResourceIdentifier(identifier.type, identifier.id))
                            .map(identifier => document.getIncluded(identifier).getModel())
                        ;
                    }
                });
            } else if (rawRelationship.data !== null) {
                Object.defineProperty(model, name, {
                    get(): Model {
                        const identifier = rawRelationship.data as RawResourceIdentifier;
                        return document.getIncluded(new ResourceIdentifier(identifier.type, identifier.id)).getModel();
                    }
                });
                // const identifier = rawRelationship.data as RawResourceIdentifier;
                // model[name] = document.getIncluded(new ResourceIdentifier(identifier.mode, identifier.id)).getModel();
            } else {
                Object.defineProperty(model, name, {
                    get(): Model {
                        return null;
                    }
                });
            }
        });

        // Create resource.
        return new Resource(rawResource.type, model);
    }
}



// WEBPACK FOOTER //
// ./src/utils/json-api/DocumentBuilder.ts